import { useRef } from 'react'

const MedicalHistorySection = ({ userInfo, setUserInfo, showTitle = true, dependant = false }) => {
  const allergyRef = useRef()
  const medicationRef = useRef()
  const conditionRef = useRef()

  const getPrefix = () => (dependant ? 'My dependant has' : 'I have')
  const getNonePrefix = () => (dependant ? 'My dependant has no' : 'I have no')
  const getPreferNotPrefix = () => 'I prefer not to say'

  return (
    <>
      {showTitle && (
        <div className="sm:col-span-6">
          <h2 className="text-xl font-semibold">Medical history</h2>
        </div>
      )}

      <div className="sm:col-span-6">
        {/* Medication allergies */}
        <div className="mt-4">
          <label className="block mb-1 font-medium">Medication allergies</label>
          <div className="flex gap-2">
            <textarea
              ref={allergyRef}
              value={userInfo?.medicalHistory?.allergies?.value || ''}
              onChange={(e) =>
                setUserInfo((prev) => ({
                  ...prev,
                  medicalHistory: {
                    ...prev.medicalHistory,
                    allergies: {
                      value: e.target.value,
                      noKnownAllergies: false,
                      preferNotToSay: false,
                    },
                  },
                }))
              }
              placeholder="Etc"
              className="mt-2 block w-1/2 rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />
          </div>
          <div className="mt-2 space-y-1">
            <label className="flex items-center">
              <input
                type="radio"
                name="allergy-option"
                checked={userInfo?.medicalHistory?.allergies?.noKnownAllergies || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      allergies: {
                        value: '',
                        noKnownAllergies: true,
                        preferNotToSay: false,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getNonePrefix()} known allergies
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="allergy-option"
                checked={userInfo?.medicalHistory?.allergies?.preferNotToSay || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      allergies: {
                        value: '',
                        noKnownAllergies: false,
                        preferNotToSay: true,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getPreferNotPrefix()}
            </label>
          </div>
        </div>

        {/* Current medications */}
        <div className="mt-4">
          <label className="block mb-1 font-medium">Current medications</label>
          <div className="flex gap-2">
            <textarea
              ref={medicationRef}
              value={userInfo?.medicalHistory?.medications?.value || ''}
              onChange={(e) =>
                setUserInfo((prev) => ({
                  ...prev,
                  medicalHistory: {
                    ...prev.medicalHistory,
                    medications: {
                      value: e.target.value,
                      takingNone: false,
                      preferNotToSay: false,
                    },
                  },
                }))
              }
              placeholder="Etc"
              className="mt-2 block w-1/2 rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />
          </div>
          <div className="mt-2 space-y-1">
            <label className="flex items-center">
              <input
                type="radio"
                name="medication-option"
                checked={userInfo?.medicalHistory?.medications?.takingNone || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      medications: {
                        value: '',
                        takingNone: true,
                        preferNotToSay: false,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getNonePrefix()} medications
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="medication-option"
                checked={userInfo?.medicalHistory?.medications?.preferNotToSay || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      medications: {
                        value: '',
                        takingNone: false,
                        preferNotToSay: true,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getPreferNotPrefix()}
            </label>
          </div>
        </div>

        {/* Pre-existing conditions */}
        <div className="my-4">
          <label className="block mb-1 font-medium">Pre-existing conditions</label>
          <div className="flex gap-2">
            <textarea
              ref={conditionRef}
              value={userInfo?.medicalHistory?.conditions?.value || ''}
              onChange={(e) =>
                setUserInfo((prev) => ({
                  ...prev,
                  medicalHistory: {
                    ...prev.medicalHistory,
                    conditions: {
                      value: e.target.value,
                      noKnownConditions: false,
                      preferNotToSay: false,
                    },
                  },
                }))
              }
              placeholder="Etc"
              className="mt-2 block w-1/2 rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
            />
          </div>
          <div className="my-2 space-y-1">
            <label className="flex items-center">
              <input
                type="radio"
                name="condition-option"
                checked={userInfo?.medicalHistory?.conditions?.noKnownConditions || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      conditions: {
                        value: '',
                        noKnownConditions: true,
                        preferNotToSay: false,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getNonePrefix()} medical conditions
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="condition-option"
                checked={userInfo?.medicalHistory?.conditions?.preferNotToSay || false}
                onChange={() =>
                  setUserInfo((prev) => ({
                    ...prev,
                    medicalHistory: {
                      ...prev.medicalHistory,
                      conditions: {
                        value: '',
                        noKnownConditions: false,
                        preferNotToSay: true,
                      },
                    },
                  }))
                }
                className="mr-2"
              />
              {getPreferNotPrefix()}
            </label>
          </div>
        </div>
      </div>
    </>
  )
}

export default MedicalHistorySection
